import * as React from "react";
import { Ban, Building2, Pencil, Plus, RotateCcw } from "lucide-react";
import type { Company, Pagination as PageMeta } from "@taskiq/shared";
import { Badge } from "@/components/ui/Badge";
import { PhoneField } from "@/components/ui/PhoneField";
import { Button } from "@/components/ui/Button";
import { useConfirm, useDiscardGuard } from "@/components/ui/confirm-context";
import { EmptyState, ErrorState, FormMessage } from "@/components/ui/Feedback";
import { Field, Form, FormSection, Input } from "@/components/ui/Field";
import { IconButton } from "@/components/ui/IconButton";
import { Identity } from "@/components/ui/Identity";
import { IconTile } from "@/components/ui/IconTile";
import { Modal } from "@/components/ui/Modal";
import { FilterBar } from "@/components/ui/FilterBar";
import { FilterSelect } from "@/components/ui/FilterSelect";
import { Pagination } from "@/components/ui/Pagination";
import { PageHeader } from "@/components/ui/PageHeader";
import { SearchInput } from "@/components/ui/SearchInput";
import { useToast } from "@/components/ui/toast-context";
import { SkeletonRows, Table, Td, TdSpan, Th, Tr } from "@/components/ui/Table";
import { TimezoneSelect, browserTimezone } from "@/components/ui/TimezoneSelect";
import { ApiError, api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PLACEHOLDER } from "@/lib/placeholders";
import { LIST_PAGE_SIZE } from "@/lib/paging";
import { SEARCH_DEBOUNCE_MS, useDebounced } from "@/lib/useDebounced";
import { useApi } from "@/lib/useApi";

interface CompanyListResponse {
  companies: Company[];
  pagination: PageMeta;
}

/** Superadmin surface - the nav entry is gated on `platform.admin`. */
export function Companies() {
  const { can } = useAuth();

  const [search, setSearch] = React.useState("");
  const [status, setStatus] = React.useState("");
  const [page, setPage] = React.useState(1);
  // The SWR key is the URL, so an undebounced box is one request per keystroke
  // and the list settles on whichever response lands last.
  const trimmed = useDebounced(search, SEARCH_DEBOUNCE_MS).trim();

  const query = new URLSearchParams({ page: String(page), pageSize: String(LIST_PAGE_SIZE) });
  if (trimmed) query.set("search", trimmed);
  // The API's filter, not this component's - see `listCompaniesQuery`. Filtering
  // the ten rows that happen to have arrived would hide matches on page two.
  if (status) query.set("is_active", status);

  const { data, error, isLoading, mutate } = useApi<CompanyListResponse>(`/companies?${query}`);

  // Narrowing the results moves the last page underneath you. Without this, a
  // search from page 3 asks for page 3 of two pages of matches and renders an
  // empty table that reads as "no companies match".
  React.useEffect(() => {
    setPage(1);
  }, [trimmed, status]);
  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<Company | null>(null);

  // Creating a customer organisation is a Visionify operation rather than a
  // permission a customer could ever be granted, which is why the route behind
  // this button gates on the role instead of a capability. `platform.admin` is
  // the capability that names exactly that set of people.
  const canCreate = can("platform.admin");

  // Whether the emptiness is the filters' doing. Without the status half, a
  // search-free "Deactivated" filter that matched nothing said "No companies
  // yet" and offered to create one, to a superadmin looking at a full table.
  const filtered = Boolean(trimmed || status);

  const confirm = useConfirm();
  const toast = useToast();
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [rowError, setRowError] = React.useState<Record<string, string>>({});

  /**
   * The tenant kill switch.
   *
   * This used to be absent here, on the argument that `is_active: false` revokes
   * every user inside the company **including the administrators who would undo
   * it**. That is true, and it is an argument for the confirmation naming the
   * consequence rather than for the control not existing: the phone has had it
   * since it was built, so the capability was already in the product and the
   * console was the client that could not exercise it. A capability on one and
   * not the other is a defect unless there is a written reason.
   *
   * Only a superadmin reaches this page, and only a superadmin can lift it -
   * `app.enforce_company_write_rules` decides that in Postgres rather than here.
   *
   * Reactivating is not confirmed. It restores access and takes nothing away,
   * the same asymmetry as close/reopen on Sites and deactivate/reactivate on
   * Team.
   */
  async function toggleActive(company: Company) {
    setRowError((prev) => {
      const next = { ...prev };
      delete next[company.id];
      return next;
    });

    if (company.is_active) {
      const ok = await confirm({
        title: `Deactivate ${company.name}?`,
        message:
          "Every user at this workspace loses access immediately, including their own " +
          "administrators - so nobody there can undo it. Their locations, appointments and " +
          "assessments stay on record.",
        confirmLabel: "Deactivate",
        cancelLabel: "Leave it active",
        danger: true,
      });
      if (!ok) return;
    }

    setBusyId(company.id);
    try {
      await api(`/companies/${company.id}`, {
        method: "PATCH",
        body: { is_active: !company.is_active },
      });
      toast.success(`${company.name} ${company.is_active ? "deactivated" : "reactivated"}`);
      await mutate();
    } catch (err) {
      setRowError((prev) => ({
        ...prev,
        [company.id]: apiErrorMessage(err, "Could not change that workspace"),
      }));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="page">
      <PageHeader
        title="Workspaces"
        description={
          canCreate
            ? "Every customer workspace on the platform, created and deactivated from here."
            : "Your workspace's own record."
        }
        actions={
          canCreate && (
            <Button icon={<Plus />} onClick={() => setCreating(true)}>
              New company
            </Button>
          )
        }
      />

      <FilterBar
        activeCount={[trimmed, status].filter(Boolean).length}
        onClear={() => {
          setSearch("");
          setStatus("");
        }}
        search={
          <SearchInput
            value={search}
            onChange={setSearch}
            label="Search workspaces by name"
            placeholder="Search workspaces"
          />
        }
      >
        {/* The same control, the same options and the same words as the Sites
            screen's. A tenant and a yard are switched off by the same button
            for the same reason, so they are not allowed to be described by two
            different pairs of adjectives. */}
        <FilterSelect
          value={status}
          onChange={setStatus}
          label="Filter by status"
          icon={<Building2 />}
          active={Boolean(status)}
          options={[
            { value: "", label: "Any status" },
            { value: "true", label: "Active" },
            { value: "false", label: "Deactivated" },
          ]}
        />
      </FilterBar>

      {error && <ErrorState error={error} onRetry={() => void mutate()} />}

      {/* Suppressed while a failure is on screen with nothing cached behind it.
          An empty table under an error banner is a header strip with no body -
          it reads as a confident "there is nothing here", which is a different
          and much more alarming claim than "we could not load this". */}
      {!(error && !data) && (
        <>
          <Table
            head={
              <>
                <Th>Company</Th>
                <Th hideBelow="sm">Contact</Th>
                <Th>Status</Th>
                <Th align="right">Actions</Th>
              </>
            }
          >
            {isLoading && (
              <SkeletonRows
                rows={2}
                columns={[
                  { kind: "identity" },
                  { kind: "text", hideBelow: "sm" },
                  { kind: "badge", width: "narrow" },
                  { kind: "blank" },
                ]}
              />
            )}

            {data && data.companies.length === 0 && (
              <tr>
                <TdSpan colSpan={4}>
                  <EmptyState
                    icon={<Building2 />}
                    title={filtered ? "No workspaces match that" : "No workspaces yet"}
                    description={
                      filtered
                        ? "Try a different name or status, or clear the filters."
                        : "A workspace is the top of the tenancy tree - locations and users live inside one."
                    }
                    action={
                      !filtered &&
                      canCreate && <Button onClick={() => setCreating(true)}>Create one</Button>
                    }
                  />
                </TdSpan>
              </tr>
            )}

            {data?.companies.map((company) => (
              <Tr key={company.id}>
                <Td>
                  <Identity
                    media={
                      <IconTile>
                        <Building2 />
                      </IconTile>
                    }
                    title={company.name}
                    subtitle={company.email ?? undefined}
                    subtitleOnlyBelow="sm"
                  />
                  {rowError[company.id] && (
                    <p role="alert" className="row-error">
                      {rowError[company.id]}
                    </p>
                  )}
                </Td>
                <Td hideBelow="sm" className="muted">
                  {company.email ?? "-"}
                </Td>
                <Td>
                  {company.is_active ? (
                    <Badge tone="ok">Active</Badge>
                  ) : (
                    <Badge tone="stop">Deactivated</Badge>
                  )}
                </Td>
                <Td align="right">
                  <div className="row-actions">
                    {canCreate && (
                      <IconButton
                        size="sm"
                        label={`Edit ${company.name}`}
                        onClick={() => setEditing(company)}
                      >
                        <Pencil />
                      </IconButton>
                    )}
                    {canCreate && company.is_active && (
                      <IconButton
                        size="sm"
                        tone="danger"
                        disabled={busyId === company.id}
                        label={`Deactivate ${company.name}`}
                        onClick={() => void toggleActive(company)}
                      >
                        <Ban />
                      </IconButton>
                    )}
                    {canCreate && !company.is_active && (
                      <IconButton
                        size="sm"
                        disabled={busyId === company.id}
                        label={`Reactivate ${company.name}`}
                        onClick={() => void toggleActive(company)}
                      >
                        <RotateCcw />
                      </IconButton>
                    )}
                  </div>
                </Td>
              </Tr>
            ))}
          </Table>

          {data && (
            <Pagination pagination={data.pagination} onPageChange={setPage} label="companies" />
          )}
        </>
      )}

      {canCreate && (
        <CreateCompanyDialog
          open={creating}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            void mutate();
          }}
        />
      )}

      {canCreate && editing && (
        <CompanyDialog
          company={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void mutate();
          }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────── edit ─────────────────────────────── */

/**
 * A tenant's contact record. Nothing here identifies the tenant.
 *
 * ── Neither the name nor the slug is editable ───────────────────────────────
 *
 * The slug is the harder of the two and it used to be a field. It is unique, it
 * appears in URLs and in every export a customer has already filed, and there is
 * no rewrite of the artefacts that carry it - so an edit here silently
 * invalidates references held outside this system. It is also invisible to a
 * customer, which means the only person it ever confuses is the superadmin
 * looking at this form.
 *
 * The name went with it for a plainer reason: it is what every other screen in
 * the console joins against when it says which tenant a site or a person belongs
 * to, and renaming a live customer from a contact form is not an edit anyone
 * should make between a phone number and a website. Both are set once, at
 * creation. Changing either is a deliberate operation and belongs behind one.
 *
 * Suspending a company **is** on this page now, but on the row rather than in
 * here - see `toggleActive`. It is a tenant-wide revocation, not a contact
 * detail, and putting it in a dialog somebody opened to fix a phone number is
 * how it gets pressed by accident.
 */
function CompanyDialog({
  company,
  onClose,
  onSaved,
}: {
  company: Company;
  onClose: () => void;
  onSaved: () => void;
}) {
  const formId = React.useId();
  const toast = useToast();
  const [form, setForm] = React.useState({
    email: company.email ?? "",
    phone: company.phone ?? "",
    website: company.website ?? "",
  });
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  const patch: Record<string, unknown> = {};
  // Emptied means cleared, which the schema expresses as null rather than "".
  if (form.email.trim() !== (company.email ?? "")) patch.email = form.email.trim() || null;
  if (form.phone.trim() !== (company.phone ?? "")) patch.phone = form.phone.trim() || null;
  if (form.website.trim() !== (company.website ?? "")) patch.website = form.website.trim() || null;

  const dirty = Object.keys(patch).length > 0;
  const requestClose = useDiscardGuard(dirty, saving, onClose);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await api(`/companies/${company.id}`, { method: "PATCH", body: patch });
      toast.success(`${company.name} saved`);
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not save that workspace"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={() => void requestClose()}
      title={company.name}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={() => void requestClose()}>
            Cancel
          </Button>
          <Button form={formId} type="submit" size="sm" loading={saving} disabled={!dirty}>
            Save changes
          </Button>
        </>
      }
    >
      <Form id={formId} onSubmit={submit}>
        <div className="form-grid">
          <Field label="Email" htmlFor="company-edit-email">
            <Input
              id="company-edit-email"
              type="email"
              placeholder={PLACEHOLDER.companyEmail}
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </Field>

          <Field label="Phone" htmlFor="company-edit-phone">
            <PhoneField
              id="company-edit-phone"
              value={form.phone}
              onChange={(phone) => setForm((f) => ({ ...f, phone }))}
            />
          </Field>

          <Field
            label="Website"
            htmlFor="company-edit-website"
            hint="Include https://"
            className="form-grid__full"
          >
            <Input
              id="company-edit-website"
              type="url"
              placeholder={PLACEHOLDER.website}
              value={form.website}
              onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
            />
          </Field>
        </div>

        <FormMessage>{error}</FormMessage>
      </Form>
    </Modal>
  );
}

/**
 * A new tenant, and the site it is entered through.
 *
 * ── Why the site is on this form ────────────────────────────────────────────
 *
 * This dialog used to ask for a company name and nothing else, and what it
 * produced could not be used. The console works one site at a time, so the
 * company switcher in the topbar has nothing to select for a company with an
 * empty site list and silently does nothing; the Sites screen only ever creates
 * into the company already selected, which can never be this one; and an
 * invitation must name a home site, so the Team screen dead-ends on its own
 * form. The row was created, appeared in this table, and was inert.
 *
 * Self sign-up never had this problem - `create_workspace` takes a company name
 * and a site name together, because one without the other is not a workspace.
 * This is the same rule on the superadmin's path, and the API now enforces it
 * (`createCompanySchema`) rather than trusting this form to remember.
 *
 * ── The slug is still derived and still invisible ───────────────────────────
 *
 * It is unique, it appears in URLs and in exports customers have already filed,
 * and `CompanyDialog` explains why it can never be edited afterwards. Showing it
 * at creation only offers a decision that cannot be revisited, about a value the
 * customer never sees. So it is derived and sent silently, and the collision the
 * field used to make visible is reported against the name - the only thing on
 * screen the reader can change.
 */
function CreateCompanyDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const formId = React.useId();
  const toast = useToast();
  const [name, setName] = React.useState("");
  const [contact, setContact] = React.useState({ email: "", phone: "", website: "" });
  const [siteName, setSiteName] = React.useState("");
  // The superadmin is provisioning somebody else's yard, so the browser's zone
  // is a starting point rather than an answer. It is seeded because a blank
  // required field on a two-field form is worse, and it is a real picker
  // because the site's calendar renders in this zone (decisions #25).
  const [timezone, setTimezone] = React.useState(browserTimezone);
  const [nameError, setNameError] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  const slug = slugify(name);

  // `browserTimezone` is a function passed to `useState` as a lazy initialiser,
  // so this captures what it produced rather than comparing against the function.
  const seededTimezone = React.useRef(timezone);

  const dirty =
    name.trim() !== "" ||
    siteName.trim() !== "" ||
    contact.email.trim() !== "" ||
    contact.phone.trim() !== "" ||
    contact.website.trim() !== "" ||
    timezone !== seededTimezone.current;
  const requestClose = useDiscardGuard(dirty, submitting, onClose);

  function reset() {
    setName("");
    setContact({ email: "", phone: "", website: "" });
    setSiteName("");
    setNameError(null);
    setError(null);
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setNameError(null);

    // `slug` is `trimmedString`-backed and rejects the empty string, so a name
    // of only punctuation would fail server-side with a message about a field
    // this form does not have.
    if (slug.length < 2) {
      setNameError("Use at least two letters or numbers.");
      return;
    }

    setSubmitting(true);
    try {
      await api("/companies", {
        method: "POST",
        body: {
          name,
          slug,
          // Empty is not the same as null. A blank optional field means "absent",
          // which the schema expresses as null rather than "".
          email: contact.email.trim() || null,
          phone: contact.phone.trim() || null,
          website: contact.website.trim() || null,
          site: { name: siteName, timezone },
        },
      });
      toast.success(`${name.trim()} created`, {
        description: "Switch to it, then invite its first administrator.",
      });
      reset();
      onCreated();
    } catch (err) {
      // Branching on the code, never the message - see lib/api.ts.
      if (err instanceof ApiError && err.status === 409) {
        setNameError("A workspace with this name already exists.");
      } else {
        setError(apiErrorMessage(err, "Could not create that workspace"));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => void requestClose()}
      title="New workspace"
      className="dialog--wide"
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={() => void requestClose()}>
            Cancel
          </Button>
          <Button form={formId} type="submit" size="sm" loading={submitting}>
            Create company
          </Button>
        </>
      }
    >
      <Form id={formId} onSubmit={submit}>
        <p className="prose prose--sm">
          A new tenant and its first site. Its first administrator is invited from the Team screen.
        </p>

        <div className="form-grid">
          <Field
            label="Workspace name"
            htmlFor="company-name"
            required
            error={nameError ?? undefined}
            hint="Set once. It is what every other screen joins against to say which tenant something belongs to."
            className="form-grid__full"
          >
            <Input
              id="company-name"
              autoFocus
              placeholder={PLACEHOLDER.companyName}
              invalid={Boolean(nameError)}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                setNameError(null);
              }}
            />
          </Field>
        </div>

        {/* Optional, and offered here because the phone has offered them since
            it was built - a superadmin provisioning a tenant usually has the
            shared mailbox in front of them, and "add it afterwards" meant a
            second trip through a second dialog. */}
        <FormSection title="Contact" description="All optional. Filled in later is fine.">
          <div className="form-grid">
            <Field
              label="Email"
              htmlFor="company-new-email"
              hint="The organisation's, not a person's."
            >
              <Input
                id="company-new-email"
                type="email"
                placeholder={PLACEHOLDER.companyEmail}
                value={contact.email}
                onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))}
              />
            </Field>

            <Field label="Phone" htmlFor="company-new-phone">
              <PhoneField
                id="company-new-phone"
                value={contact.phone}
                onChange={(phone) => setContact((c) => ({ ...c, phone }))}
              />
            </Field>

            <Field
              label="Website"
              htmlFor="company-new-website"
              hint="Include https://"
              className="form-grid__full"
            >
              <Input
                id="company-new-website"
                type="url"
                placeholder={PLACEHOLDER.website}
                value={contact.website}
                onChange={(e) => setContact((c) => ({ ...c, website: e.target.value }))}
              />
            </Field>
          </div>
        </FormSection>

        <FormSection
          title="First location"
          description="Required. A workspace with no locations cannot be opened or invited into, so both are created together."
        >
          <div className="form-grid">
            <Field
              label="Location name"
              htmlFor="company-site-name"
              required
              hint="A crew, depot, region or client location."
            >
              <Input
                id="company-site-name"
                placeholder={PLACEHOLDER.siteName}
                value={siteName}
                onChange={(event) => setSiteName(event.target.value)}
              />
            </Field>

            <Field
              label="Timezone"
              htmlFor="company-site-tz"
              required
              hint="The location's own zone."
            >
              <TimezoneSelect id="company-site-tz" value={timezone} onChange={setTimezone} />
            </Field>
          </div>
        </FormSection>

        <FormMessage>{error}</FormMessage>
      </Form>
    </Modal>
  );
}

/** Matches the `slug` shape the shared schema validates, so the form cannot submit one it will reject. */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}
